import React, { Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import {
  overlay as OverlayUtils,
  ownerDocument as OwnerDocument,
  container as getContainer,
} from '@ott/utility-overlay';

import rAF from 'raf';

const getScaleValueRegExp = /scale\((\d+(?:\.\d+)?)\)/;

import s from './Position.scss';
import cls from 'classnames/bind';
const cx = cls.bind(s);
class Position extends React.PureComponent {
  static propTypes = {
    targetFn: PropTypes.func,
    containerPadding: PropTypes.number,
    placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    isCentering: PropTypes.bool,
  };

  static defaultProps = {
    containerPadding: 0,
    placement: 'right',
    isCentering: true,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      positionLeft: null,
      positionTop: null,
      arrowOffsetLeft: null,
      arrowOffsetTop: null,
    };

    this._needsFlush = false;
    this._lastTarget = null;
  }

  componentWillReceiveProps() {
    this._needsFlush = true;
  }

  componentDidMount() {
    this.updatePosition();
    this._updateOnResize = () => {
      rAF(() => {
        this.updatePosition(true);
      });
    };
    window.addEventListener('resize', this._updateOnResize);
  }

  componentDidUpdate(prevProps) {
    if (this._needsFlush) {
      this._needsFlush = false;
      this.updatePosition(prevProps.placement !== this.props.placement);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._updateOnResize);
  }

  updatePosition(placementChanged) {
    const target = this.getTargetSafe();

    if (target === this._lastTarget && !placementChanged) {
      return;
    }

    this._lastTarget = target;

    if (!target) {
      this.setState({
        positionLeft: null,
        positionTop: null,
        arrowOffsetLeft: null,
        arrowOffsetTop: null,
      });

      return;
    }

    const overlay = ReactDOM.findDOMNode(this);
    const container = getContainer(this.props.container, OwnerDocument(this).body);

    const nextState = OverlayUtils.calcOverlayPosition({
      placement: this.props.placement,
      overlayNode: overlay,
      target,
      container,
      padding: this.props.containerPadding,
      isCentering: this.props.isCentering,
    });
    this.setState(nextState);
  }

  getTargetSafe() {
    if (!this.props.targetFn) {
      return null;
    }

    const target = this.props.targetFn(this.props);
    if (!target) {
      return null;
    }

    return target;
  }

  getScale() {
    const { transform } = document.body.style;

    if (!transform) {
      return 1;
    }

    const matches = getScaleValueRegExp.exec(transform);

    if (matches.length !== 2) {
      return 1;
    }

    const scale = Number(matches[1]);

    if (isNaN(scale)) {
      return 1;
    }

    return scale;
  }

  render() {
    const { children, placement } = this.props;
    let { positionLeft, positionTop } = this.state;

    const scale = this.getScale();

    if (scale > 0) {
      positionLeft = positionLeft / scale;
      positionTop = positionTop / scale;
    }

    const child = Children.only(children);
    return cloneElement(child, {
      style: {
        ...child.props.style,
        left: placement !== 'right' ? positionLeft : null,
        top: positionTop,
      },
    });
  }
}

export default Position;
